import { useMemo, useState, useEffect } from "react";
import { Button, Icon, Text, Tooltip } from "@ui-component-library";
import { useNavigationStore, pushMinSkogView } from '../../store/NavigationStore';
import { usePropsDataStore } from '@store/PropsDataStore';
import { useUserDataStore } from '../../store/UserDataStore';
import { sendEvent } from '../../../../api';
import ArbonicsDialog from '../ArbonicsDialog/ArbonicsDialog';
import { OffcanvasSheetWrapper, MenuButton, ProfilePopover } from "./Overlays";
import { cn } from "@utils/cn";
import { useDemoMinskogStore } from "../../store/DemoMinskogStore";
import { DEMO_PROMPTS } from "../../constants/demoPrompts";
import { VirkespriskollenModal } from "../../virkespriskollen/VirkespriskollenModal";
import { useOpenDroneCheckout } from "@components/Checkout/config/DroneCheckout/DroneCheckoutContext";
import { useOpenSubscriptionCheckout } from "@components/Checkout/config/SubscriptionCheckout/SubscriptionCheckoutContext";

import { usePlusBlockerDialog } from "@components/PlusBlockerDialog/PlusBlockerDialog.context";
import PropertySelector from "@components/PropertySelector/PropertySelector";
export default function MainNavigation({ openNavSheet, setOpenNavSheet, isMinimized: isMinimizedProp, onMinimizedChange }) {
    const { view, setView } = useNavigationStore()
    const { props: userProperties } = usePropsDataStore()
    const { userData } = useUserDataStore()
    const hasNoProperties = userProperties.length === 0
    const { openPlusBlockerDialog } = usePlusBlockerDialog();
    const { openCheckout: openDroneCheckout } = useOpenDroneCheckout();
    const { openCheckout: openSubscriptionCheckout } = useOpenSubscriptionCheckout();

    const { isDemo, DemoBlockFunctionWrapper } = useDemoMinskogStore()

    // Modals and dialogs
    const [arbonicsDialogVisible, setArbonicsDialogVisible] = useState(window.location.href.includes("#co2-interest"));
    const [openProfilePopover, setOpenProfilePopover] = useState(false);

    const [isMinimizedInternal, setIsMinimizedInternal] = useState(false);
    const isControlled = isMinimizedProp !== undefined && onMinimizedChange != null;
    const isMinimized = isControlled ? isMinimizedProp : isMinimizedInternal;
    const setIsMinimized = isControlled ? onMinimizedChange : setIsMinimizedInternal;
    const [openVirkespriskollen, setOpenVirkespriskollen] = useState(false)
    const [openPropertySelector, setOpenPropertySelector] = useState(false)
    const [propertySelectorOpenAddFirst, setPropertySelectorOpenAddFirst] = useState(false)


    useEffect(() => {
        const expandNav = () => {
            if (isMinimized && screen.width <= 992) {
                setIsMinimized(false);
            }
        }
        window.addEventListener('resize', expandNav);
        return () => window.removeEventListener('resize', expandNav);
    }, []);

    /** Deep link: min-skog/#salj-virke → Sell Timber (replaces old create-ad modal). */
    useEffect(() => {
        if (typeof window === 'undefined' || !window.location.href.includes('#salj-virke')) {
            return;
        }
        setView({ comp: 'sell-timber' });
        const url = new URL(window.location.href);
        url.hash = '';
        history.replaceState({ comp: 'sell-timber' }, '', url.toString());
    }, [setView]);

    const handleOpenAddPropertyDialog = () => {
        setPropertySelectorOpenAddFirst(true);
        setOpenPropertySelector(true);

        if (openNavSheet) { 
            setOpenNavSheet(false) 
        }
    }



    const email = Window?.user_data?.email;
    const initial = email ? email.charAt(0).toUpperCase() : "";

    const setHistory = (id) => {
        if (history.state?.comp != id) {
            history.pushState({ comp: id }, '', '')
        }
    }

    // Menu item handlers
    const handleArbonicsClick = () => {
        setArbonicsDialogVisible({ show: true })
        if (openNavSheet) { setOpenNavSheet(false) }
        sendEvent('co2_open_modal', { source: 'main_nav' })
        gtag("event", "co2_open_modal", {
            method: "site"
        });

    }


    // Menu components

    const profileLinks = useMemo(() => {
        const closeProfile = () => setOpenProfilePopover(false);
        const links = [];
        if (Window.user_data.isAdmin) {
            links.unshift({ id: 'admin', label: 'Admin', icon: 'UserCircleSimple', onClick: () => { closeProfile(); window.location.href = '/vb-admin/'; } });
        }
        if (!Window.user_data.isPremium) {
            links.push({ id: 'pricing', label: 'Uppgradera till Plus', icon: 'SparkleSimple', onClick: () => { closeProfile(); setView({ comp: 'pricing' }); setHistory('pricing'); } });
        }
        links.push(
            { id: 'settings', label: 'Inställningar', icon: 'GearSimple', onClick: () => { closeProfile(); setView({ comp: 'settings' }); setHistory('settings'); if (openNavSheet) { setOpenNavSheet(false) } } },
            { id: 'support', label: 'Support', icon: 'LifebuoySimple', onClick: () => { closeProfile(); window.location.href = Window.user_data.isPremium ? '/redirect-book-meeting/' : '/skoglig-radgivning/'; } },
            { id: 'my-ads', label: 'Mina virkesannonser', icon: 'ListStarSimple', onClick: () => { closeProfile(); pushMinSkogView('settings', { settingsTab: 'my-ads' }); if (openNavSheet) { setOpenNavSheet(false); } } },
            { id: 'logga-ut', label: 'Logga ut', icon: 'UploadSimple', onClick: () => { closeProfile(); window.location.href = '/logout/'; } },
        );
        return links;
    }, [openNavSheet]);


    const userHasAds = (userData?.ads?.ads?.length ?? 0) > 0;

    const menuItems = (noMinimized = false) => {
        const isMinimizedInternal = noMinimized ? false : isMinimized;
        const minSkogLinks = [
            { id: 'karta', label: 'Karta', icon: view?.comp === 'karta' ? 'MapTrifoldFilled' : 'MapTrifoldSimple', onClick: () => { setView({ comp: 'karta', section: view.section }); setHistory('karta'); if (openNavSheet) { setOpenNavSheet(false) } } },
            { id: 'overblick', label: 'Överblick', icon: view?.comp === 'overblick' ? 'HouseFilled' : 'HouseSimple', onClick: () => { setView({ comp: 'overblick', section: view.section }); setHistory('overblick'); if (openNavSheet) { setOpenNavSheet(false) } } },
            { id: 'overvakning', label: 'Skogsövervakning', icon: 'BroadcastSimple', 
                onClick: () => { 
                    if(!Window.user_data.isPremium && !isDemo) {
                        openPlusBlockerDialog('dfr');
                        return;
                    }
                    setView({ comp: 'overvakning' }); 
                    setHistory('overvakning'); 
                    if (openNavSheet) { 
                        setOpenNavSheet(false) 
                    } 
                }
            },
            { id: 'virkespriser', label: 'Prisjämförelse', icon: view?.comp === 'virkespriser' ? 'ScalesFilled' : 'ScalesSimple', onClick: () => { setView({ comp: 'virkespriser' }); setHistory('virkespriser'); if (openNavSheet) { setOpenNavSheet(false) } } },
            // For now the two below are on hold
            // { id: 'economy', label: 'Ekonomi', icon: 'PiggyBankSimple', onClick: () => { setView({ comp: 'economy' }); setHistory('economy'); if (openNavSheet) { setOpenNavSheet(false) } } },
            // { id: 'menu', label: 'Meny', icon: 'DotsNineSimple', onClick: () => { setView({ comp: 'menu' }); setHistory('menu') } },
        ];

        const packagesLinks=[
            { id: 'pricing', label: 'Priser & paket', icon: view?.comp === 'pricing' ? 'ChartLineFilled' : 'ChartLineSimple', onClick: () => { if(isDemo){window.open("/paket/", "_blank");} else{setView({ comp: 'pricing' }); setHistory('pricing'); if (openNavSheet) { setOpenNavSheet(false) } } } },
        ]

        const transferLinks = []
        
        if(Window.user_data.roles.includes("buyer")){
            transferLinks.unshift({ id: 'kopa-virke', label: 'Köpa virke', icon: view?.comp === 'kopa-virke' ? 'LogFilled' : 'LogSimple', href: '/kop-virke/' })
        }

        transferLinks.push(
            { id: 'salja-virke', label: 'Sälja virke', icon: view?.comp === 'sell-timber' ? 'TreeEvergreenFilled' : 'TreeEvergreenSimple', onClick: () => { if (isDemo){DemoBlockFunctionWrapper(() => { }, {"message": "På exempelfastigheten kan du inte sälja virke. Logga in för att konkurrensutsätta ditt virke, nå alla potentiella köpare och få rättvist betalt för ditt virke i din skog.", "heading": "Sälj virke från din egen skog"})}else{ mixpanel.track('Clicked salj virke min skog'); setView({ comp: 'sell-timber' }); setHistory('sell-timber'); } if (openNavSheet) { setOpenNavSheet(false) } } },
            ...(userHasAds ? [{
                id: 'my-ads',
                label: 'Mina virkesannonser',
                icon: 'ListStarSimple',
                onClick: () => {
                    pushMinSkogView('settings', { settingsTab: 'my-ads' });
                    if (openNavSheet) { setOpenNavSheet(false); }
                },
            }] : []),
            { id: 'kolkrediter', label: 'Kolkrediter', icon: 'HexagonSimple', onClick: () => { if(isDemo){DemoBlockFunctionWrapper(() => { }, {"message": "Du tittar just nu på en exempelfastighet. Här sparas inga ändringar. Logga in för att läsa mer och se om du har potential för kolkrediter i din skog samt vilka intäkter som är möjliga. Detta kräver att du är Pluskund.", "heading": "Se din kolkreditpotential"})} else{
                if(Window.user_data.isPremium){
                    handleArbonicsClick(); 
                    if (openNavSheet) { 
                        setOpenNavSheet(false) 
                    }
                } else {
                    openPlusBlockerDialog('carbon-credits');
                }}
            }},
            { id: 'marknadsinformation', label: 'Marknadsinformation', icon: view?.comp === 'marknadsinformation' ? 'NewspaperFilled' : 'NewspaperSimple', onClick: () => { if(isDemo){DemoBlockFunctionWrapper(() => { }, {"message": "Du tittar just nu på en exempelfastighet. Här visas endast en begränsad version. Logga in för att se alla händelser, läsa marknadsinformation och ta del av hela artiklar med full marknadsinsikt.", "heading": "Läs marknadsinformation"})} else{setView({ comp: 'marknadsinformation' }); setHistory('marknadsinformation'); if (openNavSheet) { setOpenNavSheet(false) }}}},
            { id: 'skoglig-rådgivning', label: 'Skoglig rådgivning', icon: 'CustomerSupportSimple', onClick: () => { if(isDemo){DemoBlockFunctionWrapper(()=>{}, {"message": "Vi besitter djup kunskap om skog, virkespriser m.m. Vi hjälper dig gärna i ditt skogsägande men tjänsten kräver att du är Pluskund.", "heading": "Skoglig expertis till stöd i ditt skogsägande"}) } else{
                if (Window.user_data.isPremium){ 
                    setView({comp: 'book-advising'}); 
                    setHistory('book-advising');
                    setOpenNavSheet(false);
                } else { 
                    openPlusBlockerDialog('forest-advisory');
                }}
            }},
            { id: 'virkespriskollen', label: 'Virkespriskollen', icon: 'TrendUp', onClick: () => {if(isDemo){DemoBlockFunctionWrapper(()=>{}, {"message": "Med vår Virkespriskoll får du hjälp att säkerställa pris och villkor för din virkesaffär.", "heading": "Vi analyserar ditt avverkningsförslag och hjälper dig få rätt betalt"}) } else{setOpenVirkespriskollen(true)}; if (openNavSheet) { setOpenNavSheet(false) } } },
            {
                id: 'drone',
                label: 'Köp drönaranalys',
                icon: 'DroneSimple',
                onClick: () => { if(isDemo){DemoBlockFunctionWrapper(()=>{}, {"message": "Du tittar just nu på en exempelfastighet. Logga in för att beställa en drönarflyging och analys för din skog. Detta kräver att du är Pluskund.", "heading": "Beställ drönarflygning för din skog"}) } else{
                    if(Window.user_data.isPremium){
                        openDroneCheckout();
                    } else {
                        openPlusBlockerDialog('drone-analysis');
                    }
                }}
            }
        )

        
        return (
            <div className="flex flex-col">
                <div className="flex flex-col gap-1">
                    {!isMinimizedInternal && <Text variant="text-xs/leading-normal/medium" className="opacity-70 text-charcoal-1000 px-2">Min Skog</Text>}
                    {minSkogLinks.map((item) => (
                        <MenuButton
                            key={item.id}
                            label={item.label}
                            iconName={item.icon}
                            isActive={view?.comp === item.id}
                            onClick={item.onClick}
                            isMinimized={isMinimizedInternal}
                            href={item.href}
                            preventMouseDownFocus={true}
                        />
                    ))}

                    {(!isMinimizedInternal) && <Text variant="text-xs/leading-normal/medium" className="opacity-70 text-charcoal-1000 px-2 pt-6">Vårt erbjudande</Text>}
                    { packagesLinks.map((item) => (
                        <MenuButton
                            key={item.id}
                            label={item.label}
                            iconName={item.icon}
                            isActive={view?.comp === item.id}
                            onClick={item.onClick}
                            isMinimized={isMinimizedInternal}
                            href={item.href}
                            preventMouseDownFocus={true}
                        />
                    ))}

                    {(!isMinimizedInternal) && <Text variant="text-xs/leading-normal/medium" className="opacity-70 text-charcoal-1000 px-2 pt-6">Tjänster</Text>}
                    { transferLinks.map((item) => (
                        <MenuButton
                            key={item.id}
                            label={item.label}
                            iconName={item.icon}
                            isActive={item.id === 'my-ads'
                                ? view?.comp === 'settings' && view?.settingsTab === 'my-ads'
                                : view?.comp === item.id}
                            onClick={item.onClick}
                            isMinimized={isMinimizedInternal}
                            href={item.href}
                            preventMouseDownFocus={true}
                        />
                    ))}
                  
                </div>
                <div className="flex-grow p-2" />
                {isDemo && <button
                        type="button"
                        onClick={() => {
                          window.open("/paket/", "_blank");
                          if (openNavSheet) { setOpenNavSheet(false) }
                        }}
                        className="flex w-full items-center gap-3 rounded-xl cursor-pointer bg-[#F3F3F3] p-3 text-left transition-opacity hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-plum-500/50 my-4"
                      >
                        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#EBEBEB]">
                          <Icon
                            name="SparkleSimple"
                            size="large"
                            className="fill-purple-plum-500"
                          />
                        </span>
                        <span className="min-w-0 flex-1">
                          <Text
                            variant="text-sm/leading-normal/medium"
                            className="block text-charcoal-500"
                          >
                            Utforska Plus
                          </Text>
                          <Text
                            variant="text-xs/leading-normal/normal"
                            className="block text-cloud-800"
                          >
                            Prova allt utan kostnad
                          </Text>
                        </span>
                      </button>
                }
                {!isDemo && hasNoProperties && (
                    <button
                        type="button"
                        onClick={handleOpenAddPropertyDialog}
                        className="flex w-full items-center gap-3 rounded-xl cursor-pointer bg-[#F3F3F3] p-3 text-left transition-opacity hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-plum-500/50 my-4"
                      >
                        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#EBEBEB]">
                          <Icon
                            name="PineTreeSimple"
                            size="large"
                            className="fill-charcoal-500"
                          />
                        </span>
                        <span className="min-w-0 flex-1">
                          <Text
                            variant="text-sm/leading-normal/medium"
                            className="block text-charcoal-500"
                          >
                            Skogsbruksplan saknas
                          </Text>
                          <Text
                            variant="text-xs/leading-normal/normal"
                            className="block text-cloud-800"
                          >
                            Lägg till din skog
                          </Text>
                        </span>
                    </button>
                )}
            </div>
        );
    };

    const menuFooter = (noMinimized = false) => {
        const isMinimizedInternal = noMinimized ? false : isMinimized;
        return (
            <div className="mt-auto">
                <ProfilePopover
                    initial={initial}
                    profileLinks={profileLinks}
                    view={view}
                    userData={Window.user_data}
                    open={openProfilePopover}
                    onOpenChange={() => DemoBlockFunctionWrapper(() => setOpenProfilePopover(!openProfilePopover), DEMO_PROMPTS.MANAGE_ACCOUNT)}
                    side="top"
                    align="center"
                    contentClassName="p-3">
                        <div className={cn('flex cursor-pointer sticky bottom-0 items-center gap-2 p-2 hover:bg-cloud-200 rounded-[10px]', isMinimizedInternal ? 'justify-center' : 'justify-start')} onClick={() => DemoBlockFunctionWrapper(() => setOpenProfilePopover(!openProfilePopover), DEMO_PROMPTS.MANAGE_ACCOUNT)} onMouseDown={(e) => e.preventDefault()}>
                            <Button variant="tertiary" size="icon" className="bg-cloud-200 rounded-md p-2 w-9 h-9 flex items-center justify-center" aria-label="Initial">
                                <Text variant="text-xs/leading-normal/medium" className='text-center'>{initial}</Text>
                            </Button>
                            {!isMinimizedInternal && <div className="flex flex-col">
                                <Text variant="text-sm/leading-none/semibold" className="truncate max-w-[180px] capitalize">{Window.user_data?.name.toLowerCase()}</Text>
                                <Text variant="text-xs/leading-none/normal" className="truncate max-w-[180px] leading-snug">{email?.toLowerCase()}</Text>
                            </div>}

                        </div>
                </ProfilePopover>
            </div>
        );
    }

    const mobileMenuItems = () => {
        const mobileMenuButton = ({ isActive, label, iconName, onClick }) => {
            return (
                <div role="button" key={label}
                    className={`flex flex-col justify-center items-center cursor-pointer rounded-lg hover:bg-cloud-200 min-w-[60.8px]`}
                    onClick={onClick}>
                    <Icon name={iconName} size="large" className={cn('mb-1-5 rounded-sm h-9 w-9', isActive && 'bg-cloud-200')} />
                    <Text variant={`${isActive ? "text-xs/leading-none/medium" : "text-xs/leading-none/normal"}`} className={cn('min-w-0 w-full truncate text-center leading-snug', isActive ? 'text-charcoal-1000' : 'text-[#1A1A1A]')}>{label}</Text>
                </div>
            )
        }
        return (<div className="flex flex-row justify-between w-full">
            {mobileMenuButton({ label: 'Karta', iconName: view?.comp === 'karta' ? 'MapTrifoldFilled' : 'MapTrifoldSimple', isActive: view?.comp === 'karta', onClick: () => { setView({ comp: 'karta', section: view.section }); setHistory('karta') } })}
            {mobileMenuButton({ label: 'Överblick', iconName: view?.comp === 'overblick' ? 'HouseFilled' : 'HouseSimple', isActive: view?.comp === 'overblick', onClick: () => { setView({ comp: 'overblick', section: view.section }); setHistory('overblick') } })}
            {mobileMenuButton({ label: 'Övervakning', iconName: 'BroadcastSimple', isActive: view?.comp === 'overvakning', onClick: () => { 
                if(!Window.user_data.isPremium && !isDemo) {
                    openPlusBlockerDialog('dfr');
                    return;
                }
                setView({ comp: 'overvakning' }); 
                setHistory('overvakning'); 
                if (openNavSheet) { 
                    setOpenNavSheet(false) 
                } 
            } })}
            {/* {mobileMenuButton({ label: 'Prisjämförelse', iconName: 'PriceComparisonSimple', isActive: view?.comp === 'virkespriser', onClick: () => { setView({ comp: 'virkespriser' }); setHistory('virkespriser') } })} */}
            {mobileMenuButton({ label: 'Meny', iconName: view?.comp === 'menu' ? 'DotsNineFilled' : 'DotsNineSimple', isActive: view?.comp === 'menu', onClick: () => { setOpenNavSheet(true) } })}
        </div>);
    }



    return (<>
        <>
            {/* Large screen navigation */}
            <div className={cn('bg-[#FBFBFB] h-screen flex-col p-2 transition-all duration-300 hidden lg:flex', isMinimized ? 'w-[52px]' : 'w-[236px]')}>
                <div className={cn('flex p-4 shrink-0 w-full', isMinimized ? 'justify-center' : 'justify-between')}>
                    {isMinimized ? (
                        <div className="relative flex items-center justify-center rounded-lg group cursor-pointer">
                            <Icon name="TreebulaSimple" className="block object-contain group-hover:opacity-0 transition-opacity" size="large" />
                            <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                <Button onClick={() => setIsMinimized(!isMinimized)} size="icon" variant="tertiary" aria-label="Öppna sidopanel">
                                    <Tooltip content="Öppna sidopanel" side="right">
                                        <Icon name="SidebarSimple" className="rounded-lg p-1-5 rotate-180" size="large" />
                                    </Tooltip>
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <>
                            <a className="pt-2" href="/">
                                <Icon name="TreebulaSimple" className="block" size="large" />
                            </a>
                            <Button onClick={() => setIsMinimized(!isMinimized)} size="icon" variant="tertiary" aria-label="Stäng sidopanel">
                                <Tooltip content="Stäng sidopanel" side="right">
                                    <Icon name="SidebarSimple" className="rounded-lg" size="large" />
                                </Tooltip>
                            </Button>
                        </>
                    )}
                </div>

                <div className="flex-grow h-full min-h-0 overflow-y-auto">
                    {menuItems()}
                </div>

                <div className="mt-auto">
                    {menuFooter()}
                </div>
            </div>
            
            {/* Small to medium screens navigation */}
            <div className="fixed bottom-0 right-0 left-0 bg-[#FBFBFB] z-[1020] lg:hidden flex justify-around px-2 pt-2 pb-4 h-[78px]">
                {/* Only way to get the border top to appear */}
                <div className="absolute top-0 left-0 right-0 h-[1px] bg-[#E6E6E6]" />
                {mobileMenuItems()}
            </div>


            {/* Offcanvas navigation */}
            <OffcanvasSheetWrapper openSheet={openNavSheet} setOpenSheet={setOpenNavSheet} menuItems={() => menuItems(true)} menuFooter={() => menuFooter(true)} />
            

        <VirkespriskollenModal setOpenVirkespriskollen={setOpenVirkespriskollen} openVirkespriskollen={openVirkespriskollen} />

        <PropertySelector
            isOpen={openPropertySelector}
            openAddPropertyInitially={propertySelectorOpenAddFirst}
            onClose={() => {
                setOpenPropertySelector(false);
                setPropertySelectorOpenAddFirst(false);
            }}
        />

        </>
        {/* ARBONICS DIALOG */}
        <ArbonicsDialog visible={arbonicsDialogVisible} hide={() => { setArbonicsDialogVisible(false) }} />
    </>
    );
}